import React, { createContext, useContext, useEffect, useState } from 'react';

// Import Theme CSS files
import '../themes/light-pro.css';
import '../themes/turkish-pro.css';
import '../themes/modern.css';
import '../themes/ocean.css';
import '../themes/light-green.css';
import '../themes/light-gray.css';
import '../themes/sunset.css';
import '../themes/classic.css';

type ThemeName = 'turkish-pro' | 'light-green' | 'light-gray' | 'light-pro' | 'modern' | 'classic' | 'ocean' | 'sunset';

interface Theme {
    name: ThemeName;
    label: string;
    description: string;
}

interface ThemeContextType {
    theme: ThemeName;
    setTheme: (name: ThemeName) => void;
    availableThemes: Theme[];
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const themes: Theme[] = [
    { name: 'light-pro', label: 'Light Professional', description: 'Clean, professional design for daylight use.' },
    { name: 'turkish-pro', label: 'Turkish Professional', description: 'Advanced professional theme for Turkish users.' },
    { name: 'modern', label: 'Modern Dark', description: 'Sleek, dark design with glassmorphism effects.' },
    { name: 'ocean', label: 'Ocean Depth', description: 'Professional maritime blue theme.' },
    { name: 'light-green', label: 'Nature Light', description: 'Soft green tones for reduced eye strain.' },
    { name: 'light-gray', label: 'Neutral Gray', description: 'Minimalist neutral workspace.' },
    { name: 'sunset', label: 'Sunset Horizon', description: 'Warm energy with purple and orange tones.' },
    { name: 'classic', label: 'Classic Simple', description: 'Traditional simple design.' }
];

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [theme, setThemeState] = useState<ThemeName>(() => {
        return (localStorage.getItem('ui-theme') as ThemeName) || 'light-pro';
    });

    useEffect(() => {
        // 1. Remove all theme classes from document
        document.documentElement.classList.remove(...themes.map(t => t.name));
        document.documentElement.classList.remove('dark');

        // 2. Add current theme class
        document.documentElement.classList.add(theme);

        // 3. Handle 'dark' class for Tailwind's dark selector
        if (['modern', 'ocean', 'sunset', 'classic'].includes(theme)) {
            document.documentElement.classList.add('dark');
        }

        // 4. Update localStorage
        localStorage.setItem('ui-theme', theme);

        // 5. Update local storage for Profile.tsx fallback (compatibility)
        localStorage.setItem('uiThemeMode', ['modern', 'ocean', 'sunset', 'classic'].includes(theme) ? 'dark' : 'light');

    }, [theme]);

    const setTheme = (name: ThemeName) => {
        setThemeState(name);
    };

    return (
        <ThemeContext.Provider value={{ theme, setTheme, availableThemes: themes }}>
            {children}
        </ThemeContext.Provider>
    );
};

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (context === undefined) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};
